import { FunctionComponent, useState, useCallback, useEffect } from 'react';
import { Layout } from 'components/Layout/Layout';
// @ts-ignore
import Head from 'next/head';
import styled, { css } from 'styled-components';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useMediaQuery } from 'react-responsive';
import { Consts as StyleConsts } from 'styles/consts';
import { useRouter } from 'next/router';
import { FlexWrapper, FlexBox } from 'components/Layout/Flex';
import { Image } from 'components/Layout/Image';
import { Spacer } from 'components/Layout/Spacer';
import { Footer } from 'components/Layout/Footer';
import { scrollTop } from 'utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { ActionButton } from '../../components/Buttons/ActionButton';
import { Header } from '../../components/Layout/Header';

const Prize2022: FunctionComponent = () => {
  const router = useRouter();
  const [isMobile, setMobile] = useState(false);

  const mobile = useMediaQuery({
    query: `(max-width: ${StyleConsts.mobileBreakPoint})`,
  });
  useEffect(() => {
    setMobile(mobile);
  }, [mobile]);

  const onRoute = useCallback(
    (path) => {
      router.push(path);
      scrollTop();
    },
    [router],
  );

  return (
    <Layout>
      <Head>
        <title>The Earth Prize - 2024</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:title" content="The Earth Prize - 2024" />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header />
      <FlexWrapper>
        <FlexBox background="#327F89" color="#F4F4F4F4" justify="center">
          {isMobile && <Spacer height={50} />}
          {!isMobile && <Spacer height={60} />}
          <H1>
            THE EARTH PRIZE
            <br />
            2024 WINNER
          </H1>
          {!isMobile && <Spacer height={10} />}
          <TeamName>FloodGate</TeamName>
          <H3>
            North Carolina School of Science and Mathematics | United States
          </H3>
          {!isMobile && <Spacer height={10} />}
          <Divider />
          {!isMobile && <Spacer height={10} />}
          <WinnerWrapper>
            <PrizeIcon />
            <div>
              <H3>WINNING SOLUTION</H3>
              <Spacer height={10} />
              <Text color="white">
                A groundbreaking web-based application utilizing advanced
                computational models to predict flood patterns.
              </Text>
            </div>
          </WinnerWrapper>
          <Spacer height={10} />
          <ActionButton
            background="#AFE0D7"
            color="#000000"
            onClick={() => onRoute('/the-earth-prize-2024/flood-gate')}
            text="none"
          >
            Read more
          </ActionButton>
        </FlexBox>
        <FlexBoxStyled background="white" color="#EDA06D" withoutPadding>
          <Image
            image="/images/scholars2024/teams/floodGate.webp"
            position="center"
            size="90%"
          />
        </FlexBoxStyled>
      </FlexWrapper>
      <RunnersWrapper id="winner-and-runners-up">
        <H2 align="center" color="black">
          THE EARTH PRIZE 2024 RUNNERS-UP
        </H2>
        <RunnersTeamsWrapper>
          <RunnerTeam color="#6FB6BB">
            <RunnerTeamImage
              image="/images/scholars2024/teams/ceres.webp"
              color="#6FB6BB"
            />
            <RunnerContent>
              <div>
                <TeamName color="white">Ceres</TeamName>
                <TeamSchool>Bahçeşehir Koleji Diyarbakir | Türkiye</TeamSchool>
                <Divider />
                <WinnerWrapper>
                  <PrizeIcon />
                  <div>
                    <H3>PROPOSED SOLUTION</H3>
                    <Text color="white">
                      A system that leverages plasma technology to enhance seed
                      germination and plant health, combating environmental
                      stressors to increase crop yield sustainably and reduce
                      reliance on chemical fertilizers.
                    </Text>
                  </div>
                </WinnerWrapper>
                <Spacer height={10} />
              </div>
              <ActionButton
                background="#fff"
                color="#6FB6BB"
                onClick={() => onRoute('/the-earth-prize-2024/ceres')}
                text="none"
              >
                Read more
              </ActionButton>
            </RunnerContent>
          </RunnerTeam>
          <RunnerTeam color="#AFE0D7">
            <RunnerTeamImage
              image="/images/scholars2024/teams/cocoMellow.webp"
              color="#AFE0D7"
              position="center 25%"
            />
            <RunnerContent>
              <div>
                <TeamName color="#327F89">CocoMellow</TeamName>
                <TeamSchool color="#327F89">i-IVY | Vietnam</TeamSchool>
                <Divider color="#327F89" />
                <WinnerWrapper>
                  <PrizeIcon />
                  <div style={{ color: '#327F89' }}>
                    <H3>PROPOSED SOLUTION</H3>
                    <Text color="#327F89">
                      A sustainable diaper using Vietnam&apos;s abundant coconut
                      coir and banana fiber.
                    </Text>
                  </div>
                </WinnerWrapper>
                <Spacer height={10} />
              </div>
              <ActionButton
                background="#327F89"
                color="#AFE0D7"
                onClick={() => onRoute('/the-earth-prize-2024/coco-mellow')}
                text="none"
              >
                Read more
              </ActionButton>
            </RunnerContent>
          </RunnerTeam>
          <RunnerTeam color="#1D8676">
            <RunnerTeamImage
              image="/images/scholars2024/teams/pebble.webp"
              color="#1D8676"
              position="center 30%"
            />
            <RunnerContent>
              <div>
                <TeamName color="white">Pebble</TeamName>
                <TeamSchool>Eton College | United Kingdom</TeamSchool>
                <Divider />
                <WinnerWrapper>
                  <PrizeIcon />
                  <div>
                    <H3>PROPOSED SOLUTION</H3>
                    <Text color="white">
                      A system that hamesses idle GPUs to create a shared
                      computing network, optimizing resource use, reducing
                      carbon emissions, and democratizing access to high-
                      powered computing while promoting environmental
                      sustainability in the tech industry.
                    </Text>
                  </div>
                </WinnerWrapper>
                <Spacer height={10} />
              </div>
              <ActionButton
                background="#fff"
                color="#1D8676"
                onClick={() => onRoute('/the-earth-prize-2024/pebble')}
                text="none"
              >
                Read more
              </ActionButton>
            </RunnerContent>
          </RunnerTeam>
        </RunnersTeamsWrapper>
      </RunnersWrapper>
      <FlexWrapper>
        <FlexBox background="#78494D" color="#fff" justify="space-between">
          <div>
            <NominatorImage
              image="/images/educators/2024/aashna.webp"
              color="#E2A375"
            />
            {isMobile ? (
              <H2 color="#fff">THE EARTH PRIZE 2024 EDUCATOR OF THE YEAR</H2>
            ) : (
              <H2 color="#fff">
                THE EARTH PRIZE
                <br />
                2024 EDUCATOR OF
                <br />
                THE YEAR
              </H2>
            )}
            <TeamName color="#E2A375">Aashna Saraf</TeamName>
            <H3Styled>CreatED I Mumbai, India</H3Styled>
            {!isMobile && <Spacer height={30} />}
          </div>
          <ActionButton
            background="#E2A375"
            color="#fff"
            text="none"
            onClick={() => onRoute('/2024-educator')}
          >
            Read more
          </ActionButton>
        </FlexBox>
        <FlexBox background="#E2A375" color="#fff" justify="space-between">
          <div>
            <MentorsImagesWrapper>
              <NominatorImage image="/images/elham.webp" color="#71494E" />
              <NominatorImage image="/images/francisca.webp" color="#71494E" />
              <NominatorImage image="/images/yoong.webp" color="#71494E" />
            </MentorsImagesWrapper>
            {isMobile ? (
              <H2 color="#fff">THE EARTH PRIZE 2024 MENTORS OF THE YEAR</H2>
            ) : (
              <H2 color="#fff">
                THE EARTH PRIZE
                <br />
                2024 MENTORS OF
                <br />
                THE YEAR
              </H2>
            )}
            <MentorName color="#724B4E">Elham Ashrafizadeh</MentorName>
            <H3Styled>University of Toronto | Canada</H3Styled>
            <MentorName color="#724B4E">Francisca Majala Mwaila</MentorName>
            <H3Styled>Kenyatta University | Kenya</H3Styled>
            <MentorName color="#724B4E">Yoong Sze Yeong</MentorName>
            <H3Styled>Nanyang Technological University | Singapore</H3Styled>
            {!isMobile && <Spacer height={30} />}
          </div>
          <ActionButton
            background="#71494E"
            color="#fff"
            text="none"
            onClick={() => onRoute('/2024-mentor')}
          >
            Read more
          </ActionButton>
        </FlexBox>
      </FlexWrapper>
      <FlexWrapper>
        <FlexBox background="#AFE0D7" color="white" withoutPadding>
          <Image image="/images/finalists.webp" />
        </FlexBox>
        <FlexBox background="#AFE0D7" color="black" justify="center">
          <Spacer height={15} />
          <H1 color="black">
            THE EARTH PRIZE
            <br />
            2024 FINALISTS
          </H1>
          <Spacer height={10} />
          <Text color="black">
            We are proud to present the second cohort of The Earth Prize
            Finalists. Each of these 10 teams of brilliant teenage students was
            selected by The Earth Prize Adjudicating Panel for their innovative
            solutions to accelerate environmental sustainability.
          </Text>
          <Spacer height={20} />
          <ActionButton
            background="#00B7BD"
            color="black"
            onClick={() => onRoute('finalists')}
          >
            MEET OUR FINALISTS
          </ActionButton>
        </FlexBox>
      </FlexWrapper>
      <FlexWrapper>
        <FlexBox background="#0AB7BC" color="#000000" withoutPadding>
          <Image image="/images/2023scholars.webp" />
        </FlexBox>
        <FlexBox background="black" color="white" justify="center">
          <H1 color="white">
            THE EARTH PRIZE
            <br />
            2024 SCHOLARS
          </H1>
          <Spacer height={10} />
          <Text color="white">
            The Earth Prize Scholars are the participants whose submissions have
            reached outstanding standards and from which The Earth Prize
            Adjudicating Panel will select the 10 finalist teams.
          </Text>
          <Spacer height={30} />
          <ActionButton
            background="white"
            color="black"
            onClick={() => onRoute('/scholars')}
          >
            Meet our Scholars
          </ActionButton>
        </FlexBox>
      </FlexWrapper>
      <FlexWrapper mobileHalfPage isfullPage id="awards-ceremony">
        <Image
          image={
            isMobile
              ? '/images/ceremony2024Mobile.webp'
              : '/images/ceremony2024.webp'
          }
          position="center"
          size={isMobile ? 'contain' : 'cover'}
        >
          <Watch onClick={() => onRoute('/the-earth-prize-2024-ceremony')}>
            Read more
            <MiniButton>
              <FontAwesomeIcon icon={faArrowRight as IconProp} />
            </MiniButton>
          </Watch>
        </Image>
      </FlexWrapper>
      <Footer fullHeight onTopClick={() => scrollTop()} />
    </Layout>
  );
};

const FlexBoxStyled = styled(FlexBox)`
  position: relative;

  p {
    position: absolute;
    bottom: 20px;
    right: 20px;
    color: white;
    font-size: 12px;

    a {
      color: white;
      text-decoration: underline;
    }
  }
`;

const CeremonyWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    max-width: 450px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;

    &::before {
      content: '';
      position: absolute;
      height: 216px;
      width: 175px;
      background-image: url('/images/whiteQuote.svg');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      transform: scaleX(-1);
      top: -150px;
      right: -140px;
    }

    &::after {
      content: '';
      position: absolute;
      height: 157px;
      width: 127px;
      bottom: -40px;
      left: -120px;
      background-image: url('/images/whiteQuote.svg');
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
    }

    @media (max-width: ${Consts.tabletBreakPoint}) {
      &::before {
        top: -150px;
        right: -30px;
        height: 133px;
        width: 108px;
      }

      &::after {
        bottom: -80px;
        left: -30px;
        height: 90px;
        width: 72px;
      }
    }
  `}
`;

const RunnersWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    min-height: 100vh;
    background: white;
    padding-top: 30px;
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: flex-end;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      background: #6fb6bb;
      display: block;
    }

    > h2 {
      @media (max-width: ${Consts.tabletBreakPoint}) {
        padding: 0 20px;
      }
    }
  `}
`;

const MentorsImagesWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    align-items: center;
    gap: 20px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      gap: 10px;
    }
  `}
`;

const Watch = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    align-items: center;
    font-size: 16px;
    margin-top: 370px;
    cursor: pointer;
    color: #00b7bd;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      margin-top: 110px;
    }
  `}
`;

const RunnersTeamsWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    align-items: stretch;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 20px;
    flex-grow: 1;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      grid-template-columns: 1fr;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      flex-grow: inherit;
    }
  `}
`;

const H1 = styled.h1`
  ${({ theme: { Consts } }) => css`
    font-size: 22px;
    text-transform: uppercase;
    font-weight: bold;
    @media (min-width: ${Consts.mobileBreakPoint}) {
      font-size: 32px;
    }
    @media (min-width: ${Consts.tabletBreakPoint}) {
      font-size: 54px;
    }
  `}
`;

const H2 = styled.h2<IText>`
  ${({ color = 'black', align = 'left', size, theme: { Consts } }) => css`
    font-size: ${size || '44px'};
    text-transform: uppercase;
    font-weight: bold;
    color: ${color};
    text-align: ${align};

    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 28px;
    }
  `}
`;

const H3 = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 5px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 14px;
    }
  `}
`;

const H3Styled = styled(H3)`
  ${({ theme: { Consts } }) => css`
    font-size: 20px;
    max-width: 600px;
    margin-bottom: 20px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 14px;
    }
  `}
`;

const Divider = styled.div<{ color?: string }>`
  ${({ theme: { Consts }, color = 'white' }) => css`
    width: 100%;
    height: 1px;
    background-color: ${color};
    margin: 15px 0 15px;
    @media (max-width: ${Consts.tabletBreakPoint}) {
      margin: 10px 0 15px;
    }
  `}
`;

const WinnerWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    align-items: flex-start;
    max-width: 400px;
    margin-bottom: 30px;

    div {
      &:nth-child(2) {
        padding-top: 5px;
      }
    }
    @media (max-width: ${Consts.tabletBreakPoint}) {
      margin-bottom: 10px;
      div {
        &:nth-child(2) {
          padding-top: 0px;
        }
      }
    }
  `}
`;

const PrizeIcon = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 38px;
    height: 38px;
    background-color: white;
    border-radius: 50%;
    background-image: url('/images/prizeIcon.svg');
    background-size: 20px;
    background-position: center;
    flex-shrink: 0;
    margin-right: 15px;
    background-repeat: no-repeat;
    @media (max-width: ${Consts.tabletBreakPoint}) {
      width: 30px;
      height: 30px;
      background-size: 15px;
    }
  `}
`;

const RunnerTeam = styled.div<{ color: string }>`
  ${({ color, theme: { Consts } }) => css`
    background-color: ${color};
    display: flex;
    flex-direction: column;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      flex-direction: row;
    }
    @media (max-width: ${Consts.mobileBreakPoint}) {
      flex-direction: column;
    }
  `}
`;

const RunnerTeamImage = styled.div<{
  image: string;
  color: string;
  position?: string;
}>`
  ${({ image, color, position, theme: { Consts } }) => css`
    width: 100%;
    height: 280px;
    background-image: url(${image});
    background-size: cover;
    background-position: ${position || 'center'};
    background-repeat: no-repeat;
    position: relative;
    flex-shrink: 0;

    // &::after {
    //   content: '';
    //   position: absolute;
    //   height: 100%;
    //   width: 100%;
    //   background-color: ${color};
    //   opacity: 0.3;
    // }

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      height: auto;
      width: 50%;
    }
    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: 220px;
      width: 100%;
    }
  `}
`;

const NominatorImage = styled.div<{ image: string; color: string }>`
  ${({ image, color, theme: { Consts } }) => css`
    position: relative;
    height: 150px;
    width: 150px;
    margin-bottom: 30px;

    &::before {
      content: '';
      position: absolute;
      height: 140px;
      width: 140px;
      background-color: ${color};
      border-radius: 40px 40px 40px 0;
      top: 15px;
      left: 15px;
    }

    &::after {
      content: '';
      position: absolute;
      height: 145px;
      width: 145px;
      border-radius: 40px 40px 40px 0;
      background-image: url(${image});
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }

    @media (max-width: ${Consts.tabletBreakPoint}) {
      height: 100px;
      width: 100px;
      margin-bottom: 20px;

      &::before {
        height: 90px;
        width: 90px;
        border-radius: 20px 20px 20px 0;
        top: 12px;
        left: 12px;
      }

      &::after {
        height: 95px;
        width: 95px;
        border-radius: 20px 20px 20px 0;
      }
    }
  `}
`;

const RunnerContent = styled.div`
  ${({ theme: { Consts } }) => css`
    padding: 20px 25px 30px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      padding: 15px 20px 60px;
    }
  `}
`;

const MentorName = styled.h2<{ color?: string }>`
  ${({ color = '#AFE0D7', theme: { Consts } }) => css`
    font-size: 20px;
    color: ${color};
    font-weight: bold;
    margin-top: 5px;
    margin-bottom: 0px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 18px;
      margin-top: 5px;
      margin-bottom: 5px;
    }
  `}
`;

const TeamName = styled.h2<{ color?: string }>`
  ${({ color = '#AFE0D7', theme: { Consts } }) => css`
    font-size: 28px;
    color: ${color};
    font-weight: bold;
    margin-top: 20px;
    margin-bottom: 10px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 18px;
      margin-top: 5px;
      margin-bottom: 5px;
    }
  `}
`;

const TeamSchool = styled.h2<{ color?: string }>`
  ${({ color = 'white', theme: { Consts } }) => css`
    font-size: 16px;
    color: ${color};
    font-weight: bold;
    margin-bottom: 15px;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      font-size: 16px;
    }
  `}
`;

interface IText {
  color?: string;
  size?: string;
  align?: string;
}

const Text = styled.p<IText>`
  ${({ color = 'black', size, theme: { Consts } }) => css`
    font-size: ${size || '18px'};
    color: ${color};

    a {
      color: ${color};
      text-decoration: underline;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 14px;
    }
  `}
`;

const MiniButton = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 60px;
    height: 36px;
    border-radius: 33px 33px 33px 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #00b7bd;
    color: black;
    margin-left: 20px;
    svg {
      width: 20px;
      height: 20px;
    }
    @media (max-width: ${Consts.mobileBreakPoint}) {
      width: 20px;
      height: 30px;
      background: transparent;
      margin-left: 10px;
      svg {
        width: 15px;
        height: 15px;
        path {
          fill: #00b7bd;
        }
      }
    }
  `}
`;

export default Prize2022;
